import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Box,
    CircularProgress,
} from '@material-ui/core';

interface DuplicateDialogProps {
    open: boolean;
    entryCount: number;
    onConfirm: (interval: 'weekly' | 'monthly', count: number, startFrom?: string) => Promise<void>;
    onClose: () => void;
}

export const DuplicateDialog: React.FC<DuplicateDialogProps> = ({
    open,
    entryCount,
    onConfirm,
    onClose,
}) => {
    const [interval, setInterval] = useState<'weekly' | 'monthly'>('monthly');
    const [count, setCount] = useState(1);
    const [startFrom, setStartFrom] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleConfirm = async () => {
        setSaving(true);
        setError('');
        try {
            await onConfirm(interval, count, startFrom || undefined);
            onClose();
        } catch {
            setError('Failed to duplicate entries.');
        } finally {
            setSaving(false);
        }
    };

    const totalCopies = entryCount * count;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Duplicate {entryCount} {entryCount === 1 ? 'entry' : 'entries'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" style={{ marginBottom: 16, color: '#64748b' }}>
                    Create copies with dates shifted forward. This will create {totalCopies} new {totalCopies === 1 ? 'entry' : 'entries'}.
                </Typography>

                <FormControl fullWidth style={{ marginBottom: 16 }}>
                    <InputLabel>Interval</InputLabel>
                    <Select
                        value={interval}
                        onChange={(e) => setInterval(e.target.value as 'weekly' | 'monthly')}
                    >
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label="Number of copies"
                    type="number"
                    fullWidth
                    value={count}
                    onChange={(e) => {
                        const v = Number(e.target.value);
                        if (v >= 1 && v <= 52) setCount(v);
                    }}
                    inputProps={{ min: 1, max: 52 }}
                    style={{ marginBottom: 16 }}
                />

                <TextField
                    label="Start from (optional)"
                    type="date"
                    fullWidth
                    value={startFrom}
                    onChange={(e) => setStartFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave blank to start one period after each entry's date"
                />

                {error && (
                    <Box style={{ marginTop: 12 }}>
                        <Typography variant="body2" style={{ color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleConfirm} color="primary" variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : `Duplicate (${totalCopies})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
